import React, { useMemo, useState, useEffect } from 'react'
import { Text, View } from 'react-native'
import { flow } from './flow'
import { BackButtonArrow } from '../../../ui/molecules'
import { CardEntryModel, routes } from '@linqpal/models'
import Wrapper from '../Wrapper'
import { Toolbar } from '../../../ui/molecules/Layout'
import { useStore } from '../../../store'
import { useInvoiceDetailsContext } from '../TabInvoice/InvoiceDetailsContext'
import { useTranslation } from 'react-i18next'
import { errorHandling } from './errorHandling'
import { ErrorAlert } from './ErrorAlert'
import AddPaymentMethodStore, {
  Source,
} from '../../../store/AddPaymentMethodStore'

export interface ErrorTypes {
  msg1: string
  msg2: string
  type: string
  action?: () => void
}

export default ({ navigation, route, source }) => {
  const { t } = useTranslation('global')
  const fromInvoiceLink = source === Source.INVOICE_LINK
  const showModal = route.params?.showModal
  const { setCardSuccess, setInvoicePaymentMethod, setPayInvoiceModal } =
    useInvoiceDetailsContext()
  const { addingFromInvoice } = AddPaymentMethodStore

  const [currentScreen, setCurrentScreen] = useState(flow.initial)
  const [creditCardModel, setCreditCardModel] = useState<any | null>(
    CardEntryModel.create({}),
  )
  const [error, setError] = useState<ErrorTypes>({
    msg1: '',
    msg2: '',
    type: '',
  })
  const withInvoice = useMemo(() => {
    return addingFromInvoice(source)
  }, [source, addingFromInvoice])

  const {
    userStore,
    screensStore: { paymentMethodsStore, updateScreenMenuTop },
  } = useStore()

  useEffect(() => {
    updateScreenMenuTop(true)
  }, [updateScreenMenuTop])

  const toolbar = useMemo(
    () => (
      <Toolbar
        leftComponent={
          <BackButtonArrow
            showModal={showModal}
            onPress={
              flow[currentScreen].previous
                ? () => setCurrentScreen(flow[currentScreen].previous)
                : undefined
            }
          />
        }
        rightComponent={<View style={{ width: 60 }} />}
        title={t('AddCard.link-a-card')}
        titleStyle={{ alignItems: 'center' }}
      />
    ),
    [currentScreen, showModal, t],
  )

  const onAction = (goBack = false) => {
    if (!goBack) {
      if (flow[currentScreen].next) {
        setCurrentScreen(flow[currentScreen].next)
      } else {
        if (creditCardModel.canSave && !creditCardModel.isSending) {
          creditCardModel.startSending()
          routes.company
            .addPaymentCard(creditCardModel.toJSON())
            .then(async (response) => {
              creditCardModel.stopSending()
              if (response.result === 'ok') {
                userStore.setLocalSettings({ onBoarded: true })
                userStore.fetchUser()
                await paymentMethodsStore.fetchPaymentMethods()
                setCreditCardModel(null)
                if (withInvoice) {
                  const p = paymentMethodsStore.paymentMethods
                  setInvoicePaymentMethod({
                    ...p[p.length - 1],
                    newlyAdded: true,
                  })
                  setPayInvoiceModal(true)
                  navigation.goBack()
                } else {
                  setCardSuccess(true)
                  navigation.goBack()
                }
              }
            })
            .catch((err) => {
              creditCardModel.stopSending()
              console.log(err, err.response)
              errorHandling(t, setError, err, setCurrentScreen)
            })
        }
      }
    } else {
      updateScreenMenuTop(false)
      navigation.goBack()
    }
  }

  const Component = flow[currentScreen].component
  return (
    <Wrapper
      containerStyle={
        fromInvoiceLink ? { width: '100%', alignItems: 'center' } : undefined
      }
      toolbar={toolbar}
    >
      <View>
        <Text style={{ fontSize: 19, fontWeight: '500' }}>
          {flow[currentScreen].title}
        </Text>
        <Component onAction={onAction} model={creditCardModel} />
        <ErrorAlert
          msg1={error.msg1}
          msg2={error.msg2}
          action={error.action}
          type={error.type}
          visible={!!error.type}
          onClose={() => {
            setCurrentScreen(flow.initial)
            setCreditCardModel(CardEntryModel.create({}))
            setError({
              msg1: '',
              msg2: '',
              type: '',
              action: undefined,
            })
          }}
        />
      </View>
    </Wrapper>
  )
}
